import React, { useEffect, useRef, useState } from 'react';
import { Target, UserCheck, Send, Check, ArrowRight, Zap } from 'lucide-react';
import { processStages, siteConfig } from '../../data/mock';

const iconMap = {
  Target: Target,
  UserCheck: UserCheck,
  Send: Send
};

const ProcessSection = () => {
  const [activeStage, setActiveStage] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [autoTransitionComplete, setAutoTransitionComplete] = useState(false);
  const sectionRef = useRef(null);
  const autoTransitionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Auto-transition effect - runs once when section becomes visible
  useEffect(() => {
    if (isVisible && !autoTransitionComplete) {
      // Start auto-transition after section is visible
      autoTransitionRef.current = setInterval(() => {
        setActiveStage((prev) => {
          if (prev >= processStages.length - 1) {
            // Reached last stage, stop auto-transition
            clearInterval(autoTransitionRef.current);
            setAutoTransitionComplete(true);
            return prev;
          }
          return prev + 1;
        });
      }, 5000); // 5 second interval

      return () => {
        if (autoTransitionRef.current) {
          clearInterval(autoTransitionRef.current);
        }
      };
    }
  }, [isVisible, autoTransitionComplete]);

  // Handle manual stage selection - stops auto-transition
  const handleStageClick = (index) => {
    // Stop auto-transition when user manually clicks
    if (autoTransitionRef.current) {
      clearInterval(autoTransitionRef.current);
    }
    setAutoTransitionComplete(true);
    setActiveStage(index);
  };

  return (
    <section id="product" ref={sectionRef} className="py-16 sm:py-20 bg-gradient-to-b from-[var(--bg-page)] to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-14">
          <div className="inline-flex items-center gap-2 bg-[var(--brand-teal-50)] border border-[var(--brand-teal-100)] rounded-full px-4 py-2 mb-5">
            <Zap className="w-4 h-4 text-[var(--brand-teal-600)]" />
            <span className="text-sm font-medium text-[var(--brand-teal-700)]">How We Do It</span>
          </div>
          <h2 className="heading-1 mb-4">
            From Discovery to Outreach in <span className="gradient-text">30 Minutes</span>
          </h2>
          <p className="body-large max-w-3xl mx-auto">
            Our three-stage intelligent workflow automates 20+ manual steps, letting you focus 
            on what matters most—building real connections.
          </p>
        </div>

        {/* Stage Navigation Pills */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-8 sm:mb-10">
          {processStages.map((stage, index) => (
            <button
              key={stage.id}
              onClick={() => handleStageClick(index)}
              className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full font-medium text-sm sm:text-base transition-all duration-300 ${
                activeStage === index
                  ? 'bg-[var(--brand-teal-700)] text-white shadow-lg shadow-[var(--brand-teal-600)]/20'
                  : 'bg-white text-[var(--text-secondary)] border border-gray-200 hover:border-[var(--brand-teal-300)] hover:bg-[var(--brand-teal-50)]'
              }`}
            >
              <span className="hidden sm:inline">{stage.stage}: </span>{stage.title}
            </button>
          ))}
        </div>

        {/* Active Stage Details */}
        <div
          className={`transition-all duration-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {processStages.map((stage, index) => {
            const IconComponent = iconMap[stage.icon];
            return (
              <div
                key={stage.id}
                className={`transition-all duration-500 ${
                  activeStage === index ? 'block' : 'hidden'
                }`}
              >
                <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl shadow-[var(--brand-teal-100)]/50 border border-[var(--brand-teal-100)] overflow-hidden">
                  <div className="grid md:grid-cols-2">
                    {/* Left Content */}
                    <div className="p-5 sm:p-8 md:p-10">
                      <div className="flex items-center gap-3 sm:gap-4 mb-5">
                        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br from-[var(--brand-teal-500)] to-[var(--brand-teal-400)] flex items-center justify-center shadow-lg flex-shrink-0">
                          <IconComponent className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                        </div>
                        <div>
                          <span className="text-xs sm:text-sm font-semibold text-[var(--brand-teal-600)] uppercase tracking-wide">
                            {stage.stage}
                          </span>
                          <h3 className="heading-2 text-lg sm:text-xl">{stage.title}</h3>
                        </div>
                      </div>

                      <p className="body-large text-sm sm:text-base mb-5">{stage.description}</p>

                      <div className="space-y-2 sm:space-y-3 mb-6">
                        {stage.features.map((feature, i) => (
                          <div key={i} className="flex items-center gap-2 sm:gap-3">
                            <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[var(--brand-teal-100)] flex items-center justify-center flex-shrink-0">
                              <Check className="w-3 h-3 sm:w-4 sm:h-4 text-[var(--brand-teal-600)]" />
                            </div>
                            <span className="body-medium text-sm sm:text-base">{feature}</span>
                          </div>
                        ))}
                      </div>

                      {/* Time comparison - always side by side */}
                      <div className="flex gap-3 sm:gap-4">
                        <div className="bg-[var(--brand-teal-50)] rounded-lg sm:rounded-xl px-3 sm:px-4 py-2 sm:py-3 flex-1">
                          <span className="text-[10px] sm:text-xs font-medium text-[var(--brand-teal-600)] uppercase">SuperHunt</span>
                          <p className="text-base sm:text-lg font-bold text-[var(--brand-teal-700)]">{stage.duration}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg sm:rounded-xl px-3 sm:px-4 py-2 sm:py-3 flex-1">
                          <span className="text-[10px] sm:text-xs font-medium text-gray-500 uppercase">Manual</span>
                          <p className="text-base sm:text-lg font-bold text-gray-400 line-through">{stage.manualTime}</p>
                        </div>
                      </div>
                    </div>

                    {/* Right Visual */}
                    <div className="bg-gradient-to-br from-[var(--brand-teal-600)] to-[var(--brand-teal-700)] p-3 sm:p-4 md:p-6 flex items-center justify-center">
                      <div className="w-full">
                        {/* Product Screenshot */}
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl overflow-hidden border border-white/20 shadow-2xl">
                          <img 
                            src={stage.image} 
                            alt={`${stage.title} - SuperHunt Interface`}
                            className="w-full h-auto object-contain"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-8 sm:mt-10">
          <a
            href={siteConfig.calendlyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex items-center gap-2 group w-full sm:w-auto justify-center"
          >
            See the Full Process in Action
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
